import { map } from 'underscore';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import {
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';

import { StatusIcon } from './status-icon';
import { useScopeBarStyles } from '../styles';

const TrailItem = ({
  entity, renderSeparator, active, shouldHideStatusIcon,
}) => {
  const styles = useScopeBarStyles();

  return (
    <>
      <Tooltip content={entity.name} relationship="inaccessible">
        <span className={mergeClasses('trail-item',styles.trailItem, active && styles.activeTrailItem)}>
          {entity.status && !shouldHideStatusIcon && <StatusIcon status={entity.status} />}
          <span className={mergeClasses('trail-item-text',styles.trailItemText)}>{entity.name}</span>
        </span>
      </Tooltip>
      {renderSeparator && <span className={mergeClasses('trail-separator', styles.trailItemSeparator)}>/</span>}
    </>
  );
};

TrailItem.propTypes = {
  entity: PropTypes.shape({
    name: PropTypes.string.isRequired,
    status: PropTypes.string,
  }).isRequired,
  renderSeparator: PropTypes.bool,
  active: PropTypes.bool.isRequired,
  shouldHideStatusIcon: PropTypes.bool.isRequired,
};

TrailItem.defaultProps = {
  renderSeparator: false,
};

export const ScopeTrail = ({
  rootEntityTypeDisplayName, selectedEntity, i18n, shouldHideStatusIcon, hasRootEntityFilter,
}) => {
  const createTrail = (entity) => {
    const trail = [];
    let currentEntity = entity;
    while (currentEntity) {
      trail.push(currentEntity);
      currentEntity = currentEntity.parentEntity;
    }
    return trail;
  };

  const renderTrail = useCallback(() => {
    const trail = createTrail(selectedEntity);
    const reversedTrail = trail.reverse();

    return map(reversedTrail, (entity, index) => {
      const renderSeparator = reversedTrail.length > 1
        && index < reversedTrail.length - 1;

      const active = index === reversedTrail.length - 1;

      return (
        <TrailItem
          key={index}
          entity={entity}
          renderSeparator={renderSeparator}
          active={active}
          shouldHideStatusIcon={shouldHideStatusIcon}
        />
      );
    });
  }, [selectedEntity, shouldHideStatusIcon]);

  const renderDefaultTrail = useCallback(() => (
    rootEntityTypeDisplayName
      ? (
        <TrailItem
          entity={{
            name: hasRootEntityFilter ? i18n.getString(_TL_('Filtered {{entityType}}'), { entityType: rootEntityTypeDisplayName })
              : i18n.getString(_TL_('All {{entityType}}'), { entityType: rootEntityTypeDisplayName }),
          }}
          active
        />
      )
      : null
  ), [rootEntityTypeDisplayName, i18n, hasRootEntityFilter]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {
        selectedEntity
          ? renderTrail()
          : renderDefaultTrail()
      }
    </>
  );
};

ScopeTrail.propTypes = {
  rootEntityTypeDisplayName: PropTypes.string.isRequired,
  selectedEntity: PropTypes.shape({
    name: PropTypes.string.isRequired,
    status: PropTypes.string,
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  shouldHideStatusIcon: PropTypes.bool,
  hasRootEntityFilter: PropTypes.bool,
};

ScopeTrail.defaultProps = {
  selectedEntity: null,
  shouldHideStatusIcon: false,
  hasRootEntityFilter: false,
};
